import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "gatsby"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Fade from "@mui/material/Fade"
import { FaWhatsapp } from "react-icons/fa"

import { useT } from "i18n/translations"
import { alpha, palette } from "theme/colors"
import { fontDisplay } from "theme/fonts"
import {
  anchorId,
  isAnchor,
  isTopAnchor,
  scrollToSection,
  scrollToTop,
} from "utils/navLinks"

const HEADER_HEIGHT = 72

/**
 * Header variants
 *
 * - "bar"     — nav sits inline in the top bar (desktop), collapsing to the
 *               overlay below the `md` breakpoint where five items no longer
 *               fit. This is the ACSolar default: a one-page site whose nav is
 *               a section index, so it should always be visible.
 * - "overlay" — the original miquinhas behaviour: nothing but a hamburger,
 *               opening a full-page frosted menu at every breakpoint. Kept so
 *               the component stays usable for image-led sites.
 */
const VARIANTS = { BAR: "bar", OVERLAY: "overlay" }

const Header = (props) => {
  const {
    variant = VARIANTS.BAR,
    scrolled,
    lightHeader,
    lang,
    navItems = [],
    siteSettings = {},
    allLanguages = [],
    announcementBar,
  } = props

  const tr = useT(lang)
  const announcementRef = useRef(null)
  const isBar = variant === VARIANTS.BAR

  const quoteUrl = siteSettings.quoteUrl || "#contactos"
  const whatsappNumber = (
    siteSettings.whatsapp ||
    process.env.GATSBY_WHATSAPP ||
    ""
  ).replace(/[^\d]/g, "")

  const [menuOpen, setMenuOpen] = useState(false)
  const [announcementHeight, setAnnouncementHeight] = useState(0)
  const [activeId, setActiveId] = useState("")

  const mainNavItems = navItems.filter((i) => i.type !== "cta")
  const ctaItems = navItems.filter((i) => i.type === "cta")
  const currentLang = allLanguages.find(
    (l) => l.code === lang?.toUpperCase(),
  ) || { code: "PT", flag: "🇵🇹", urlPrefix: "" }

  const headerTop = announcementBar ? announcementHeight : 0

  const closeMenu = useCallback(() => setMenuOpen(false), [])

  useEffect(() => {
    if (!menuOpen || typeof document === "undefined") return undefined
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false)
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [menuOpen])

  // Lock body scroll while the overlay is open.
  useEffect(() => {
    if (typeof document === "undefined") return undefined
    const prev = document.body.style.overflow
    if (menuOpen) document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [menuOpen])

  useEffect(() => {
    if (!announcementBar || !announcementRef.current) {
      setAnnouncementHeight(0)
      return undefined
    }
    const measure = () =>
      setAnnouncementHeight(announcementRef.current?.offsetHeight || 0)
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [announcementBar])

  // Scroll-spy: highlight the nav item whose section is in view. Only useful
  // for the inline bar, and only for anchor targets.
  useEffect(() => {
    if (!isBar || typeof window === "undefined") return undefined

    const ids = mainNavItems
      .filter((i) => !isTopAnchor(i?.link?.url))
      .map((i) => anchorId(i?.link?.url))
      .filter(Boolean)
    if (!ids.length) return undefined

    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean)
    if (!sections.length) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (visible) setActiveId(visible.target.id)
      },
      { rootMargin: `-${HEADER_HEIGHT + 8}px 0px -55% 0px`, threshold: [0.1, 0.5] },
    )

    sections.forEach((s) => observer.observe(s))
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBar, navItems])

  const handleAnchorClick = (url) => (event) => {
    if (!isAnchor(url)) return
    event.preventDefault()
    setMenuOpen(false)

    if (isTopAnchor(url)) {
      scrollToTop()
      setActiveId("")
      return
    }

    const id = anchorId(url)
    scrollToSection(id, HEADER_HEIGHT + headerTop)
    setActiveId(id)
  }

  // Bar transparency — transparent at top, solid once scrolled.
  const headerBg = menuOpen ? "transparent" : isBar || scrolled ? alpha.headerBar : "transparent"

  const textColor = palette.text

  /* ── inline bar nav item ─────────────────────────────────────────── */
  const barLinkSx = (active = false) => ({
    color: textColor,
    textDecoration: "none",
    fontSize: "13px",
    fontWeight: active ? 700 : 500,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    height: HEADER_HEIGHT,
    px: 0.25,
    opacity: active ? 1 : 0.78,
    transition: "opacity 0.2s, color 0.2s",
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 22,
      height: "2px",
      bgcolor: palette.accent,
      borderRadius: "1px",
      transform: active ? "scaleX(1)" : "scaleX(0)",
      transformOrigin: "center",
      transition: "transform 0.25s ease",
    },
    "&:hover": { opacity: 1 },
    "&:hover::after": { transform: "scaleX(1)" },
  })

  const soonBadgeSx = (small = false) => ({
    fontSize: small ? "9px" : "0.6875rem",
    fontFamily: "inherit",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    px: small ? 0.75 : 1.25,
    py: small ? 0.15 : 0.35,
    borderRadius: "100px",
    bgcolor: alpha.accentSoft,
    color: palette.accent,
    flexShrink: 0,
    ml: 0.75,
  })

  const renderBarItem = (item, i) => {
    const url = item?.link?.url || ""
    const label = item?.link?.title || ""
    const id = anchorId(url)
    const active = Boolean(id) && id === activeId

    if (item.type === "soon") {
      return (
        <Box
          key={i}
          aria-disabled="true"
          sx={{
            ...barLinkSx(false),
            opacity: 0.4,
            cursor: "default",
            pointerEvents: "none",
            "&::after": { display: "none" },
          }}
        >
          {label}
          <Box component="span" sx={soonBadgeSx(true)}>
            {tr.nav.soon}
          </Box>
        </Box>
      )
    }

    if (isAnchor(url)) {
      return (
        <Box
          key={i}
          component="a"
          href={url}
          onClick={handleAnchorClick(url)}
          aria-current={active ? "true" : undefined}
          sx={barLinkSx(active)}
        >
          {label}
        </Box>
      )
    }

    return (
      <Box key={i} component={Link} to={url} sx={barLinkSx(false)}>
        {label}
      </Box>
    )
  }

  /* ── overlay nav item (mobile + overlay variant) ─────────────────── */
  const overlayLinkSx = (accent = false) => ({
    color: accent ? palette.accent : palette.text,
    textDecoration: "none",
    fontSize: {
      xs: "clamp(1.75rem, 6vw, 2.5rem)",
      md: "clamp(2.25rem, 4vw, 3.25rem)",
    },
    fontFamily: fontDisplay,
    fontWeight: 400,
    lineHeight: 1.1,
    letterSpacing: "-0.5px",
    opacity: accent ? 1 : 0.85,
    py: { xs: 0.75, md: 1 },
    minHeight: 44,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    width: "100%",
    transition: "opacity 0.2s, color 0.2s",
    "&:hover": {
      opacity: 1,
      color: accent ? palette.accentLight : palette.accent,
    },
  })

  const renderOverlayItem = (item, i) => {
    const url = item?.link?.url || ""
    const label = item?.link?.title || ""

    if (item.type === "soon") {
      return (
        <Box
          key={i}
          aria-disabled="true"
          sx={{
            ...overlayLinkSx(),
            gap: { xs: 1, md: 1.5 },
            flexWrap: "wrap",
            cursor: "default",
            pointerEvents: "none",
            opacity: 0.45,
            "&:hover": { opacity: 0.45, color: palette.text },
          }}
        >
          {label}
          <Box component="span" sx={soonBadgeSx()}>
            {tr.nav.soon}
          </Box>
        </Box>
      )
    }

    if (isAnchor(url)) {
      return (
        <Box
          key={i}
          component="a"
          href={url}
          onClick={handleAnchorClick(url)}
          sx={overlayLinkSx()}
        >
          {label}
        </Box>
      )
    }

    return (
      <Box
        key={i}
        component={Link}
        to={url}
        onClick={closeMenu}
        sx={overlayLinkSx()}
      >
        {label}
      </Box>
    )
  }

  /* ── shared pieces ───────────────────────────────────────────────── */
  const quoteButton = (overlay = false) => {
    const anchor = isAnchor(quoteUrl)
    return (
      <Button
        {...(anchor
          ? { component: "a", href: quoteUrl, onClick: handleAnchorClick(quoteUrl) }
          : { component: Link, to: quoteUrl, onClick: closeMenu })}
        sx={{
          bgcolor: palette.accent,
          // Dark ink on the accent: white on #21B1BB is only 2.6:1 (fails AA),
          // the page ground gives 7.3:1.
          color: palette.background,
          border: "none",
          borderRadius: "100px",
          px: overlay ? { xs: 3.5, md: 5 } : { xs: 2, sm: 2.5, md: 3 },
          py: overlay ? 1.75 : "9px",
          fontSize: overlay ? { xs: "0.9375rem", md: "1.0625rem" } : "13px",
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: overlay ? "none" : "uppercase",
          whiteSpace: "nowrap",
          minHeight: overlay ? 48 : 40,
          boxShadow: overlay ? "0 4px 24px rgba(0,0,0,0.15)" : "none",
          "&:hover": { bgcolor: palette.accentDark },
          transition: "background-color 0.2s",
        }}
      >
        {overlay ? tr.header.quoteFree : tr.header.quote} →
      </Button>
    )
  }

  const whatsappButton = () => {
    if (!whatsappNumber) return null
    return (
      <Box
        component="a"
        href={`https://wa.me/${whatsappNumber}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={tr.header.whatsapp}
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "50%",
          border: `1.5px solid ${textColor}`,
          color: textColor,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          transition: "background-color 0.2s, color 0.2s, border-color 0.2s",
          "&:hover": {
            bgcolor: palette.accent,
            borderColor: palette.accent,
            color: palette.background,
          },
        }}
      >
        <FaWhatsapp aria-hidden="true" />
      </Box>
    )
  }

  const languageSwitcher = (mode = "header") => {
    if (allLanguages.length <= 1) return null
    const inOverlay = mode === "overlay"

    return (
      <Box
        component="nav"
        aria-label="Language"
        sx={{ display: "flex", alignItems: "center", gap: inOverlay ? 2 : 0.5, flexShrink: 0 }}
      >
        {allLanguages.map((l, index) => (
          <React.Fragment key={l.code}>
            {index > 0 && (
              <Box
                component="span"
                sx={{ color: textColor, opacity: 0.35, fontSize: "12px", userSelect: "none" }}
              >
                |
              </Box>
            )}
            <Box
              component={Link}
              to={l.urlPrefix || "/"}
              onClick={closeMenu}
              sx={{
                color:
                  l.code === lang?.toUpperCase() ? textColor : alpha.textMuted,
                textDecoration: "none",
                fontSize: inOverlay ? "1rem" : "12px",
                fontWeight: l.code === lang?.toUpperCase() ? 600 : 500,
                letterSpacing: "0.5px",
                minWidth: 28,
                minHeight: 44,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": { color: textColor },
              }}
            >
              {l.code}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    )
  }

  const hamburger = () => (
    <Box
      component="button"
      onClick={() => setMenuOpen((v) => !v)}
      aria-label={menuOpen ? tr.header.closeMenu : tr.header.openMenu}
      aria-expanded={menuOpen}
      sx={{
        // In the bar variant the hamburger only exists below `md`.
        display: isBar ? { xs: "flex", md: "none" } : "flex",
        alignItems: "center",
        gap: "8px",
        background: "none",
        border: "none",
        cursor: "pointer",
        color: textColor,
        fontFamily: "inherit",
        p: 1,
        lineHeight: 1,
        minWidth: 44,
        minHeight: 44,
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: 20, height: 14, position: "relative", flexShrink: 0 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              height: "2px",
              bgcolor: "currentColor",
              borderRadius: "1px",
              top:
                i === 0 ? 0 : i === 1 ? "calc(50% - 0.75px)" : "calc(100% - 1.5px)",
              transformOrigin: "center",
              transition: "transform 0.25s ease, opacity 0.2s",
              ...(menuOpen && i === 0 && { transform: "translateY(6.5px) rotate(45deg)" }),
              ...(menuOpen && i === 1 && { opacity: 0 }),
              ...(menuOpen && i === 2 && { transform: "translateY(-6.5px) rotate(-45deg)" }),
            }}
          />
        ))}
      </Box>
      {!isBar && (
        <Box
          component="span"
          sx={{
            fontSize: "15px",
            fontWeight: 500,
            letterSpacing: "0.5px",
            display: { xs: "none", sm: "inline" },
          }}
        >
          {menuOpen ? tr.header.close : tr.header.menu}
        </Box>
      )}
    </Box>
  )

  return (
    <>
      {announcementBar && (
        <Box
          ref={announcementRef}
          sx={{
            bgcolor: palette.surface,
            color: palette.text,
            textAlign: "center",
            py: "10px",
            px: 2,
            fontSize: "0.8125rem",
            letterSpacing: "0.3px",
            position: "relative",
            zIndex: 1301,
          }}
        >
          {announcementBar}
        </Box>
      )}

      <Box
        component="header"
        sx={{
          position: "fixed",
          top: headerTop,
          left: 0,
          right: 0,
          height: HEADER_HEIGHT,
          zIndex: 1300,
          bgcolor: headerBg,
          backdropFilter: !menuOpen && (scrolled || isBar) ? "blur(8px)" : "none",
          borderBottom:
            isBar && !menuOpen ? `1px solid ${palette.border}` : "1px solid transparent",
          transition: "background-color 0.35s ease, top 0.2s ease",
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
        }}
      >
        {/* Logo */}
        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
          <Box
            component={Link}
            to={currentLang.urlPrefix + "/"}
            onClick={(e) => {
              closeMenu()
              if (typeof window !== "undefined" && window.location.pathname === "/") {
                e.preventDefault()
                scrollToTop()
                setActiveId("")
              }
            }}
            sx={{ display: "inline-flex", alignItems: "center" }}
          >
            <img
              src="/images/logo.png"
              alt={siteSettings.siteTitle || "AC Solar"}
              style={{
                height: 38,
                width: "auto",
                // Light ground — the mark is used as supplied.
                filter: "none",
              }}
            />
          </Box>
        </Box>

        {/* Inline nav — bar variant, md and up */}
        {isBar && (
          <Box
            component="nav"
            aria-label="Main"
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              gap: { md: 2.5, lg: 4 },
              flex: 1,
              minWidth: 0,
            }}
          >
            {mainNavItems.map(renderBarItem)}
          </Box>
        )}

        {/* Spacer for the overlay variant, which has no inline nav */}
        {!isBar && <Box sx={{ flex: 1, minWidth: 0 }} />}

        {/* Right cluster */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            ml: isBar ? 0 : "auto",
          }}
        >
          <Box sx={{ display: menuOpen ? { xs: "none", sm: "flex" } : "flex" }}>
            {languageSwitcher("header")}
          </Box>

          <Box
            sx={{
              display: menuOpen ? "none" : { xs: "none", sm: "inline-flex" },
            }}
          >
            {quoteButton(false)}
          </Box>

          <Box sx={{ display: menuOpen ? "none" : { xs: "none", sm: "inline-flex" } }}>
            {whatsappButton()}
          </Box>

          {hamburger()}
        </Box>
      </Box>

      {/* Full-page overlay — the whole menu in "overlay", mobile only in "bar" */}
      <Fade in={menuOpen} timeout={350}>
        <Box
          role="dialog"
          aria-modal="true"
          aria-label={tr.header.openMenu}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1299,
            display: menuOpen ? "flex" : "none",
            flexDirection: "column",
            height: "100dvh",
            maxHeight: "100dvh",
            pt: `${headerTop + HEADER_HEIGHT}px`,
            pb: "max(20px, env(safe-area-inset-bottom))",
            overflow: "hidden",
            bgcolor: alpha.menuOverlay,
            backdropFilter: "blur(24px) saturate(110%)",
            WebkitBackdropFilter: "blur(24px) saturate(110%)",
          }}
        >
          <Box
            component="nav"
            aria-label="Main"
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 0.5, md: 0.75 },
              width: "100%",
              maxWidth: 720,
              mx: "auto",
              px: { xs: 3, sm: 4, md: 6 },
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {mainNavItems.map(renderOverlayItem)}
            {ctaItems.map((item, i) => {
              const url = item?.link?.url || ""
              return isAnchor(url) ? (
                <Box
                  key={`cta-${i}`}
                  component="a"
                  href={url}
                  onClick={handleAnchorClick(url)}
                  sx={overlayLinkSx(true)}
                >
                  {item.link.title}
                </Box>
              ) : (
                <Box
                  key={`cta-${i}`}
                  component={Link}
                  to={url}
                  onClick={closeMenu}
                  sx={overlayLinkSx(true)}
                >
                  {item.link.title}
                </Box>
              )
            })}
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              width: "100%",
              maxWidth: 1400,
              mx: "auto",
              px: { xs: 3, sm: 4, md: 6 },
              pt: 2,
            }}
          >
            {languageSwitcher("overlay")}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: "auto" }}>
              {whatsappButton()}
              {quoteButton(true)}
            </Box>
          </Box>
        </Box>
      </Fade>
    </>
  )
}

export default Header
